import type { NativeGitReview, NativeGitStatus } from './git';
import { nativeDesktopFrameOperations } from './native-desktop-operation';
import type { WorkspaceEntry } from './workspace';

export type WorkspaceReadyFrame = { type: 'ready'; latest?: number };
export type WorkspacePongFrame = { type: 'pong' };
export type WorkspaceErrorFrame = { type: 'error'; message: string };
export type WorkspaceRequestErrorFrame = {
  type: 'workspace_request_error';
  request_id: string;
  message: string;
  /** For a failure the user can fix: `unconfigured`/`unavailable` and what is missing. */
  code?: string;
  reason?: string;
};
export type WorkspaceSnapshotFrame = { type: 'workspace_snapshot'; patch: string };
export type WorkspaceCheckoutReadyFrame = {
  type: 'workspace_ready';
  workspace_root: string;
  /** The commit checked out, and its branch: the base of a checkout that named none. */
  head_commit_sha?: string;
  head_branch?: string | null;
};
/** A TCP port the workspace's processes listen on, and the process that opened it. */
export type WorkspacePort = { port: number; process: string | null };
export type WorkspaceFileChunkFrame = {
  type: 'workspace_file_chunk';
  request_id: string;
  content: string;
  offset: number;
  bytes_read: number;
  size: number;
};

type CoreCorrelatedResponseFrame =
  | WorkspaceRequestErrorFrame
  | WorkspaceFileChunkFrame
  | { type: 'workspace_directory'; request_id: string; entries: WorkspaceEntry[] }
  | { type: 'workspace_ports'; request_id: string; ports: WorkspacePort[] }
  | { type: 'workspace_git_review'; request_id: string; review: NativeGitReview }
  | { type: 'workspace_git_status'; request_id: string; status: NativeGitStatus }
  | {
      type: 'workspace_run_command';
      request_id: string;
      output: { stdout: string; stderr: string; exitCode: number };
    }
  | { type: 'workspace_git_action_completed'; request_id: string }
  | { type: 'workspace_handoff_return'; request_id: string; path: string; branch: string | null }
  | { type: 'workspace_cloud_pushed'; request_id: string }
  | { type: 'workspace_text_file'; request_id: string; content: string }
  | { type: 'workspace_binary_file'; request_id: string; content: string }
  | { type: 'workspace_archive'; request_id: string; content: string }
  | { type: 'workspace_archive_restored'; request_id: string }
  | { type: 'workspace_file_written'; request_id: string }
  | { type: 'workspace_file_removed'; request_id: string }
  | { type: 'terminal_spawned'; request_id: string; terminal_id: string };

type NativeDesktopResponseType =
  (typeof nativeDesktopFrameOperations)[keyof typeof nativeDesktopFrameOperations]['responseType'];

/** A native Desktop operation's answer; `decodeNativeDesktopResponse` checks its payload. */
type NativeOnlyResponseFrame = {
  type: Exclude<NativeDesktopResponseType, CoreCorrelatedResponseFrame['type']>;
  request_id: string;
} & Record<string, unknown>;

export type WorkspaceCorrelatedResponseFrame =
  | CoreCorrelatedResponseFrame
  | NativeOnlyResponseFrame;

const NATIVE_DESKTOP_RESPONSE_TYPES: ReadonlySet<string> = new Set(
  Object.values(nativeDesktopFrameOperations).map((operation) => operation.responseType),
);

export type WorkspacePushFrame =
  | { type: 'agent_output'; seq: number; payload: string }
  | { type: 'terminal_output'; terminal_id: string; data: string }
  | { type: 'terminal_exit'; terminal_id: string; code: number | null }
  | { type: 'workspace_git_changed'; workspace_root: string };

export type WorkspaceUncorrelatedResponseFrame =
  | WorkspaceSnapshotFrame
  | WorkspaceCheckoutReadyFrame;

export type WorkspaceServerFrame =
  | WorkspaceReadyFrame
  | WorkspacePongFrame
  | WorkspaceErrorFrame
  | WorkspaceCorrelatedResponseFrame
  | WorkspacePushFrame
  | WorkspaceUncorrelatedResponseFrame;

function record(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('Workspace server returned an invalid frame.');
  }
  return value as Record<string, unknown>;
}

function field<T extends 'string' | 'number' | 'array' | 'record'>(
  value: Record<string, unknown>,
  name: string,
  kind: T,
): unknown {
  const current = value[name];
  const valid =
    kind === 'array'
      ? Array.isArray(current)
      : kind === 'record'
        ? typeof current === 'object' && current !== null && !Array.isArray(current)
        : typeof current === kind;
  if (!valid) throw new Error(`Invalid workspace response field: ${name}.`);
  return current;
}

/** Parses and validates every server frame consumed by remote workspace clients. */
export function parseWorkspaceServerFrame(input: unknown): WorkspaceServerFrame {
  const value = record(typeof input === 'string' ? JSON.parse(input) : input);
  const type = field(value, 'type', 'string') as WorkspaceServerFrame['type'];
  if (type === 'ready') {
    if (value['latest'] !== undefined) field(value, 'latest', 'number');
  } else if (type === 'pong') return value as WorkspacePongFrame;
  else if (type === 'error') field(value, 'message', 'string');
  else if (type === 'workspace_snapshot') field(value, 'patch', 'string');
  else if (type === 'workspace_ready') field(value, 'workspace_root', 'string');
  else if (type === 'agent_output') {
    field(value, 'seq', 'number');
    field(value, 'payload', 'string');
  } else if (type === 'terminal_output') {
    field(value, 'terminal_id', 'string');
    field(value, 'data', 'string');
  } else if (type === 'terminal_exit') {
    field(value, 'terminal_id', 'string');
    if (value['code'] !== null) field(value, 'code', 'number');
  } else if (type === 'workspace_git_changed') {
    field(value, 'workspace_root', 'string');
  } else {
    field(value, 'request_id', 'string');
    if (type === 'workspace_request_error') field(value, 'message', 'string');
    else if (type === 'workspace_file_chunk') {
      field(value, 'content', 'string');
      field(value, 'offset', 'number');
      field(value, 'bytes_read', 'number');
      field(value, 'size', 'number');
    } else if (type === 'workspace_directory') field(value, 'entries', 'array');
    else if (type === 'workspace_ports') field(value, 'ports', 'array');
    else if (type === 'workspace_git_review') field(value, 'review', 'record');
    else if (type === 'workspace_git_status') field(value, 'status', 'record');
    else if (type === 'workspace_run_command') field(value, 'output', 'record');
    else if (
      type === 'workspace_text_file' ||
      type === 'workspace_binary_file' ||
      type === 'workspace_archive'
    ) {
      field(value, 'content', 'string');
    } else if (type === 'terminal_spawned') field(value, 'terminal_id', 'string');
    else if (type === 'workspace_handoff_return') {
      field(value, 'path', 'string');
      if (value['branch'] !== null) field(value, 'branch', 'string');
    } else if (
      type !== 'workspace_git_action_completed' &&
      type !== 'workspace_cloud_pushed' &&
      type !== 'workspace_archive_restored' &&
      type !== 'workspace_file_written' &&
      type !== 'workspace_file_removed' &&
      !NATIVE_DESKTOP_RESPONSE_TYPES.has(type)
    ) {
      throw new Error(`Unsupported workspace response frame: ${String(type)}.`);
    }
  }
  return value as WorkspaceServerFrame;
}
